import React, { useState, useEffect } from 'react';
import './App.css';
import useLakeTemperatures from './hooks/useLakeTemperatures';
import LakeStation from './components/LakeStation';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [selectedDataSource, setSelectedDataSource] = useState(null);
  const [mapView, setMapView] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Add this state

  // Use the custom hook to fetch real API data
  const { temperatureData, loading, error } = useLakeTemperatures();

  const dataSources = [
    {
      id: 'bafu',
      name: 'Bundesamt für Umwelt (BAFU)',
      description: 'Offizielle hydrologische Daten der Schweizer Bundesverwaltung',
      url: 'https://environment.ld.admin.ch/.well-known/void/dataset/hydro?lang=de',
      dataTypes: ['Wassertemperatur', 'Pegel', 'Abfluss', 'Grundwasser'],
      coverage: 'Schweizweit',
      updateFrequency: 'Stündlich',
      status: 'Aktiv',
      locations: 450
    },
    {
      id: 'alplakes',
      name: 'EAWAG Alplakes',
      description: 'Spezialisierte Daten für Alpine Seen und Forschung',
      url: 'https://www.alplakes.eawag.ch',
      dataTypes: ['Seetemperatur', 'Wasserqualität', 'Satellitendaten', 'Modellierung'],
      coverage: 'Alpine Seen',
      updateFrequency: 'Täglich',
      status: 'Aktiv',
      locations: 50
    },
    {
      id: 'meteoswiss',
      name: 'MeteoSchweiz',
      description: 'Meteorologische und hydrologische Messungen',
      url: 'https://www.meteoschweiz.admin.ch',
      dataTypes: ['Niederschlag', 'Temperatur', 'Verdunstung', 'Schnee'],
      coverage: 'Schweizweit',
      updateFrequency: '10 Minuten',
      status: 'Aktiv',
      locations: 280
    },
    {
      id: 'wsl',
      name: 'WSL - Eidg. Forschungsanstalt',
      description: 'Waldhydrologie und Berggewässer-Forschung',
      url: 'https://www.wsl.ch',
      dataTypes: ['Waldhydrologie', 'Gebirgsgewässer', 'Schnee', 'Erosion'],
      coverage: 'Bergregionen',
      updateFrequency: 'Täglich',
      status: 'Aktiv',
      locations: 120
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Filter data based on search term
    if (searchTerm) {
      const filtered = temperatureData.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.location.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(temperatureData);
    }
  }, [searchTerm, temperatureData]);

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setMobileMenuOpen(false); // Close mobile menu when navigating
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openDataSourceModal = (source) => {
    setSelectedDataSource(source);
  };

  const closeDataSourceModal = () => {
    setSelectedDataSource(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Enhanced coordinate mapping for all water bodies
  const getLocationCoordinates = (waterBody, location) => {
    const locationMap = {
      'Zürichsee': { lat: 47.3667, lng: 8.55 },
      'Genfersee': { lat: 46.4, lng: 6.5 },
      'Lac Léman': { lat: 46.4, lng: 6.5 },
      'Hallwillersee': { lat: 47.3, lng: 8.2 },
      'Aare': { lat: 46.9, lng: 7.4 },
      'Rhein': { lat: 47.6, lng: 7.6 },
      'Rhône': { lat: 46.2, lng: 7.0 },
      'Reuss': { lat: 47.0, lng: 8.3 },
      'Limmat': { lat: 47.4, lng: 8.5 },
      'Thur': { lat: 47.5, lng: 8.8 },
      'Inn': { lat: 46.8, lng: 10.0 },
      'Ticino': { lat: 46.2, lng: 8.8 },
      'Tresa': { lat: 46.0, lng: 8.9 },
      'Emme': { lat: 47.0, lng: 7.7 },
      'Saane': { lat: 46.8, lng: 7.2 },
      'Doubs': { lat: 47.4, lng: 7.0 },
      'Birs': { lat: 47.5, lng: 7.6 }
    };
    
    // Try to find exact match or fallback to water body type
    const coords = locationMap[waterBody] || locationMap[location];
    return coords || { lat: 46.8, lng: 8.2 }; // Default to center of Switzerland
  };

  const getTemperatureColor = (temp) => {
    if (temp === null || temp === undefined) return '#9e9e9e';
    if (temp > 20) return '#ff6b6b';
    if (temp > 15) return '#ffa726';
    if (temp > 10) return '#42a5f5';
    return '#90caf9';
  };

  const getWaterBodyStats = () => {
    if (temperatureData.length === 0) return null;
    
    const validTemps = temperatureData.filter(item => item.temperature !== null).map(item => item.temperature);
    const avgTemp = validTemps.length > 0 ? (validTemps.reduce((a, b) => a + b) / validTemps.length) : 0;
    const maxTemp = validTemps.length > 0 ? Math.max(...validTemps) : 0;
    const minTemp = validTemps.length > 0 ? Math.min(...validTemps) : 0;
    
    return {
      total: temperatureData.length,
      withData: validTemps.length,
      avgTemp: avgTemp.toFixed(1),
      maxTemp: maxTemp.toFixed(1),
      minTemp: minTemp.toFixed(1)
    };
  };

  const stats = getWaterBodyStats();

  return (
    <div className="app">
      {/* Responsive Navbar */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <a href="#home" className="logo">
            AquaOrca
          </a>
          
          {/* Hamburger Menu Button */}
          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Navigation menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          
          <ul className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li>
              <a 
                href="#home" 
                className={activeSection === 'home' ? 'active' : ''}
                onClick={() => scrollToSection('home')}
              >
                Übersicht
              </a>
            </li>
            <li>
              <a 
                href="#all-data" 
                className={activeSection === 'all-data' ? 'active' : ''}
                onClick={() => scrollToSection('all-data')}
              >
                Alle Gewässer
              </a>
            </li>
            <li>
              <a 
                href="#data" 
                className={activeSection === 'data' ? 'active' : ''}
                onClick={() => scrollToSection('data')}
              >
                Datenquellen
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={() => setMobileMenuOpen(false)}
        ></div>
      )}

      {/* Hero Section */}
      <section id="home" className="hero">
        <h1>Wassertemperaturen der Schweiz</h1>
        <p>
          Live-Daten von über {stats?.total || 0} Gewässer-Messstationen in der ganzen Schweiz. 
          Von Bergseen bis zu grossen Flüssen - alle aktuellen Temperaturen auf einen Blick.
        </p>
        
        {stats && (
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">{stats.total}</span>
              <span className="stat-label">Messstationen</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.avgTemp}°C</span>
              <span className="stat-label">Durchschnitt</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.maxTemp}°C</span>
              <span className="stat-label">Wärmster</span>
            </div>
          </div>
        )}
      </section>

      {/* Temperature Stations Overview */}
      <section id="temperatures" className="temperature-section">
        <h2>Highlights - Besondere Gewässer</h2>
        {loading ? (
          <div className="loading-container">
            <div className="loading"></div>
            <p>Lade aktuelle Gewässerdaten... 🌊</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <h3>🚫 Fehler beim Laden der Daten</h3>
            <p>{error.message}</p>
            <p>Bitte versuchen Sie es später erneut oder überprüfen Sie Ihre Internetverbindung.</p>
          </div>
        ) : (
          <div className="highlights-container">
            {(() => {
              const validStations = temperatureData.filter(station => station.temperature !== null);
              
              if (validStations.length === 0) {
                return <p>Keine aktuellen Temperaturdaten verfügbar.</p>;
              }

              // Get highlights based on different criteria
              const warmest = validStations.reduce((max, station) => 
                station.temperature > max.temperature ? station : max
              );
              
              const coldest = validStations.reduce((min, station) => 
                station.temperature < min.temperature ? station : min
              );

              // Get diverse water bodies (lakes vs rivers)
              const lakes = validStations.filter(station => 
                station.name.toLowerCase().includes('see') || 
                station.name.toLowerCase().includes('lac')
              );
              
              const rivers = validStations.filter(station => 
                !station.name.toLowerCase().includes('see') && 
                !station.name.toLowerCase().includes('lac')
              );

              // Major water bodies (popular/well-known locations)
              const majorWaterBodies = validStations.filter(station =>
                ['zürichsee', 'genfersee', 'lac léman', 'bodensee', 'thunersee', 'brienzersee', 'vierwaldstättersee']
                  .some(major => station.name.toLowerCase().includes(major.toLowerCase()))
              );

              // Build highlights array
              const highlights = [];
              
              // Always include warmest and coldest
              highlights.push({...warmest, category: 'Wärmstes Gewässer'});
              if (coldest.id !== warmest.id) {
                highlights.push({...coldest, category: 'Kältestes Gewässer'});
              }

              // Add best of major water bodies
              if (majorWaterBodies.length > 0) {
                const bestMajor = majorWaterBodies.sort((a, b) => b.temperature - a.temperature)[0];
                if (!highlights.find(h => h.id === bestMajor.id)) {
                  highlights.push({...bestMajor, category: 'Grösster See in den Daten'});
                }
              }

              // Add warmest lake if different
              if (lakes.length > 0) {
                const warmestLake = lakes.sort((a, b) => b.temperature - a.temperature)[0];
                if (!highlights.find(h => h.id === warmestLake.id)) {
                  highlights.push({...warmestLake, category: 'Wärmster See'});
                }
              }

              // Add warmest river if different
              if (rivers.length > 0) {
                const warmestRiver = rivers.sort((a, b) => b.temperature - a.temperature)[0];
                if (!highlights.find(h => h.id === warmestRiver.id)) {
                  highlights.push({...warmestRiver, category: 'Wärmster Fluss'});
                }
              }

              // Fill remaining slots with next warmest unique locations
              const remainingSlots = 6 - highlights.length;
              if (remainingSlots > 0) {
                const remaining = validStations
                  .filter(station => !highlights.find(h => h.id === station.id))
                  .sort((a, b) => b.temperature - a.temperature)
                  .slice(0, remainingSlots)
                  .map(station => ({...station, category: 'Warm'}));
                
                highlights.push(...remaining);
              }

              return (
                <div className="temperature-stations">
                  {highlights.slice(0, 6).map((station, index) => (
                    <div key={station.id} className="highlight-station">
                      <div className="highlight-category">{station.category}</div>
                      <div className="highlight-content">
                        <div className="station-info">
                          <h3 className="station-name">{station.name}</h3>
                          <p className="station-location">{station.location}</p>
                        </div>
                        <div className="temperature-display">
                          <div 
                            className="temperature-value"
                            style={{ 
                              backgroundColor: getTemperatureColor(station.temperature),
                              color: station.temperature > 15 ? '#fff' : '#333'
                            }}
                          >
                            {station.temperature !== null ? `${station.temperature.toFixed(1)}°C` : 'N/A'}
                          </div>
                          <div className="last-update">
                            {station.lastUpdate}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </section>

      {/* All Water Bodies Section - Modernized */}
      <section id="all-data" className="all-locations">
        <div className="locations-header">
          <div className="header-content">
            <h2>Alle Schweizer Gewässer</h2>
            <p className="section-subtitle">
              Entdecken Sie die aktuellen Temperaturen aller {temperatureData.length} überwachten Gewässer
            </p>
          </div>
          
          <div className="location-controls">
            <div className="search-container">
              <div className="search-input-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Nach Gewässer oder Ort suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchTerm('')}
                    title="Suche löschen"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            <div className="view-controls">
              <button 
                className={`view-btn ${mapView === 'grid' ? 'active' : ''}`}
                onClick={() => setMapView('grid')}
                title="Gitteransicht"
              >
                ⊞
              </button>
              <button 
                className={`view-btn ${mapView === 'list' ? 'active' : ''}`}
                onClick={() => setMapView('list')}
                title="Listenansicht"
              >
                ☰
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-container modern">
            <div className="loading-spinner"></div>
            <div className="loading-text">
              <h3>Lade Gewässerdaten...</h3>
              <p>Abrufen aktueller Temperaturen von allen Messstationen</p>
            </div>
          </div>
        ) : (
          <>
            <div className="data-summary-enhanced">
              <div className="summary-stats">
                <div className="summary-item">
                  <span className="summary-number">{filteredData.length}</span>
                  <span className="summary-label">Angezeigte Gewässer</span>
                </div>
                <div className="summary-item">
                  <span className="summary-number">{temperatureData.filter(s => s.temperature !== null).length}</span>
                  <span className="summary-label">Mit aktuellen Daten</span>
                </div>
                {stats && (
                  <div className="summary-item">
                    <span className="summary-number">{stats.avgTemp}°C</span>
                    <span className="summary-label">Durchschnittstemperatur</span>
                  </div>
                )}
              </div>
              
              {searchTerm && (
                <div className="search-info">
                  <span className="search-results">
                    {filteredData.length} Ergebnisse für "{searchTerm}"
                  </span>
                </div>
              )}
            </div>

                        {/* Quick Filter Chips */}
            <div className="quick-filters">
              <h4>Schnellfilter</h4>
              <div className="filter-chips">
                <button 
                  className={`filter-chip ${searchTerm === '' ? 'active' : ''}`}
                  onClick={() => setSearchTerm('')}
                >
                  Alle ({temperatureData.length})
                </button>
                <button 
                  className={`filter-chip ${searchTerm === 'see' ? 'active' : ''}`}
                  onClick={() => setSearchTerm('see')}
                >
                  Seen ({temperatureData.filter(s => s.name.toLowerCase().includes('see')).length})
                </button>
                <button 
                  className={`filter-chip ${searchTerm === 'rhein' ? 'active' : ''}`}
                  onClick={() => setSearchTerm('rhein')}
                >
                  Rhein ({temperatureData.filter(s => s.name.toLowerCase().includes('rhein')).length})
                </button>
                <button 
                  className={`filter-chip ${searchTerm === 'aare' ? 'active' : ''}`}
                  onClick={() => setSearchTerm('aare')}
                >
                  Aare ({temperatureData.filter(s => s.name.toLowerCase().includes('aare')).length})
                </button>
              </div>
            </div>
            
            <div className={`locations-container ${mapView}`}>
              {filteredData.length > 0 ? (
                filteredData.map((station) => (
                  <div key={station.id} className="location-card modern">
                    <div className="card-header">
                      <div className="location-info">
                        <h3 className="station-name">{station.name}</h3>
                        <div className="location-meta">
                          <span className="location-pin">📍</span>
                          <span className="location-text">{station.location}</span>
                        </div>
                      </div>
                      
                      <div className="temperature-display-modern">
                        {station.temperature !== null ? (
                          <>
                            <div 
                              className="temp-value-large"
                              style={{ 
                                backgroundColor: getTemperatureColor(station.temperature),
                                color: station.temperature > 15 ? '#fff' : '#333'
                              }}
                            >
                              {station.temperature.toFixed(1)}°C
                            </div>
                            <div className="temp-status">
                              {station.temperature > 20 ? '🔥 Warm' : 
                               station.temperature > 15 ? '🌡️ Mild' : 
                               station.temperature > 10 ? '❄️ Kühl' : '🧊 Kalt'}
                            </div>
                          </>
                        ) : (
                          <div className="temp-no-data">
                            <span className="no-data-icon">⚠️</span>
                            <span className="no-data-text">Keine Daten</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="card-details">
                      <div className="detail-grid">
                        <div className="detail-item">
                          <span className="detail-icon">🕒</span>
                          <div className="detail-content">
                            <span className="detail-label">Letzte Messung</span>
                            <span className="detail-value">{station.lastUpdate}</span>
                          </div>
                        </div>
                        
                        <div className="detail-item">
                          <span className="detail-icon">💧</span>
                          <div className="detail-content">
                            <span className="detail-label">Gewässertyp</span>
                            <span className="detail-value">
                              {station.name.toLowerCase().includes('see') || station.name.toLowerCase().includes('lac') ? 'See' : 'Fluss'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {station.temperature !== null && (
                        <div className="temperature-trend">
                          <div className="trend-bar">
                            <div 
                              className="trend-fill"
                              style={{ 
                                width: `${Math.min((station.temperature / 28) * 100, 100)}%`,
                                backgroundColor: getTemperatureColor(station.temperature)
                              }}
                            ></div>
                          </div>
                          <div className="trend-labels">
                            <span>0°C</span>
                            <span>28°C</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results-modern">
                  <div className="no-results-icon">🔍</div>
                  <h3>Keine Gewässer gefunden</h3>
                  <p>
                    Für "{searchTerm}" wurden keine passenden Gewässer gefunden.
                  </p>
                  <button 
                    className="clear-search-btn"
                    onClick={() => setSearchTerm('')}
                  >
                    Alle Gewässer anzeigen
                  </button>
                </div>
              )}
            </div>
          
          </>
        )}
      </section>

      {/* Enhanced Data Sources */}
      <section id="data" className="data-sources">
        <h2>Datenquellen</h2>
        <p className="data-intro">
          Unsere Live-Daten stammen direkt von der AquaOrca API, die Daten von verschiedenen 
          Schweizer Umweltbehörden aggregiert und in Echtzeit bereitstellt.
        </p>
        
        <div className="api-info">
          <h3>🔗 Live API-Endpunkt</h3>
          <p><strong>URL:</strong> <code>https://aquaorca.etter.work/api/getCurrentData.php</code></p>
          <p><strong>Aktualisierung:</strong> Automatisch alle 5 Minuten</p>
          <p><strong>Datenformat:</strong> JSON mit Gewässername, Standort, Temperatur und Zeitstempel</p>
        </div>
        
        <div className="data-sources-grid">
          {dataSources.map((source) => (
            <div key={source.id} className="data-source-card">
              <div className="source-header">
                <h3>{source.name}</h3>
                <span className={`status ${source.status.toLowerCase()}`}>
                  {source.status}
                </span>
              </div>
              
              <p className="source-description">{source.description}</p>
              
              <div className="source-stats">
                <div className="stat">
                  <span className="stat-number">{source.locations}</span>
                  <span className="stat-label">Messstationen</span>
                </div>
                <div className="stat">
                  <span className="stat-number">{source.updateFrequency}</span>
                  <span className="stat-label">Aktualisierung</span>
                </div>
              </div>
              
              <div className="source-actions">
                <button 
                  className="btn-details"
                  onClick={() => openDataSourceModal(source)}
                >
                  Details anzeigen
                </button>
                <a 
                  href={source.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-external"
                >
                  Website besuchen
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Data Source Modal - Popup Style */}
      {selectedDataSource && (
        <div className="popup-overlay" onClick={closeDataSourceModal}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <div className="popup-title-section">
                <h3>{selectedDataSource.name}</h3>
                <span className={`status-badge ${selectedDataSource.status.toLowerCase()}`}>
                  <span className="status-dot"></span>
                  {selectedDataSource.status}
                </span>
              </div>
              <button className="popup-close" onClick={closeDataSourceModal}>
                ✕
              </button>
            </div>
            
            <div className="popup-body">
              <p className="popup-description">{selectedDataSource.description}</p>
              
              <div className="popup-stats">
                <div className="popup-stat">
                  <span className="popup-stat-icon">📍</span>
                  <div>
                    <div className="popup-stat-number">{selectedDataSource.locations}</div>
                    <div className="popup-stat-label">Messstationen</div>
                  </div>
                </div>
                <div className="popup-stat">
                  <span className="popup-stat-icon">🔄</span>
                  <div>
                    <div className="popup-stat-number">{selectedDataSource.updateFrequency}</div>
                    <div className="popup-stat-label">Updates</div>
                  </div>
                </div>
                <div className="popup-stat">
                  <span className="popup-stat-icon">🌍</span>
                  <div>
                    <div className="popup-stat-number">{selectedDataSource.coverage}</div>
                    <div className="popup-stat-label">Abdeckung</div>
                  </div>
                </div>
              </div>
              
              <div className="popup-section">
                <h4>📊 Verfügbare Datentypen</h4>
                <div className="popup-tags">
                  {selectedDataSource.dataTypes.map((type, index) => (
                    <span key={index} className="popup-tag">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="popup-actions">
                <a 
                  href={selectedDataSource.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="popup-btn-primary"
                >
                  🔗 Website besuchen
                </a>
                <button 
                  className="popup-btn-secondary"
                  onClick={closeDataSourceModal}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <p>&copy; 2025 AquaOrca - Live Wassertemperaturen der Schweiz</p>
          <p>Daten werden alle 5 Minuten automatisch aktualisiert</p>
          {stats && (
            <p>📊 Live: {stats.total} Messstationen | Durchschnitt: {stats.avgTemp}°C</p>
          )}
        </div>
      </footer>
    </div>
  );
}

export default App;
